import { z } from "zod";

import { getOptionConstraints, optionRfqSizes } from "#/config/orders";
import { type UnderlyingSymbol } from "#/types/markets";

const createInstrumentSizeSchema = ({
  underlying,
  nettedPositions,
  hasNoGuardRails,
  isRfq,
  isBuy,
  isShortError,
}: Omit<ValidateOptionSizeParams, "size">) => {
  const { minSize, maxSize } = getOptionConstraints(underlying);
  const minimumSize = isRfq ? optionRfqSizes[underlying][0] : minSize;
  const maximumSize = hasNoGuardRails ? Infinity : maxSize;

  return z.coerce
    .number()
    .min(minimumSize, {
      message: isShortError
        ? `Min size is ${minimumSize}`
        : `Minimum size is ${minimumSize}.`,
    })
    .refine(
      (value) => {
        const inputValue = isBuy ? value : -value;
        const netPosition = nettedPositions + inputValue;

        return netPosition >= -maximumSize && netPosition <= maximumSize;
      },
      {
        message: isShortError
          ? `Max ${maximumSize} ${underlying}`
          : `Max net position violation. ${maximumSize} ${underlying} max.`,
      },
    );
};

export interface ValidateOptionSizeParams {
  size: string;
  underlying: UnderlyingSymbol;
  nettedPositions: number;
  hasNoGuardRails: boolean;
  isRfq?: boolean;
  isBuy?: boolean;
  isShortError?: boolean;
}

export const validateOptionSize = ({
  size,
  underlying,
  nettedPositions,
  hasNoGuardRails,
  isRfq,
  isBuy,
  isShortError,
}: ValidateOptionSizeParams): string => {
  if (!size || !underlying) {
    return "";
  }

  const schema = createInstrumentSizeSchema({
    underlying,
    nettedPositions,
    hasNoGuardRails,
    isRfq,
    isBuy,
    isShortError,
  });
  const result = schema.safeParse(size);

  if (result.success) {
    return "";
  }

  const {
    _errors: [minimumSizeError],
  } = result.error.format();
  return minimumSizeError;
};
